import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";

const TYPES = [
  { v: "article", l: "Article" },
  { v: "analysis", l: "Analysis" },
  { v: "fact_check", l: "Fact-Check" },
];

export default function Studio() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "article", title: "", summary: "" });
  const [filter, setFilter] = useState("");

  const load = () => api.get("/content").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/content", form);
      toast.success("Content created");
      setOpen(false);
      setForm({ type: "article", title: "", summary: "" });
      window.location.href = `/command-center/studio/${data.id}`;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const shown = filter ? items.filter((i) => i.type === filter) : items;

  return (
    <div className="p-6">
      <PageHeader title="Article & Analysis Studio" subtitle="Produce articles, analysis and fact-checks.">
        <select className={`${inputCls} w-40`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="studio-type-filter">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <Btn data-testid="new-content-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New</Btn>
      </PageHeader>

      <Card>
        <table className="w-full text-sm" data-testid="content-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Updated</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {shown.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-zinc-600">No content yet.</td></tr>}
            {shown.map((c) => (
              <tr key={c.id} className="hover:bg-zinc-900/50">
                <td className="px-4 py-2.5 font-medium">{c.title}</td>
                <td className="px-4 py-2.5 text-zinc-400 capitalize">{c.type.replace("_", "-")}</td>
                <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2.5 text-zinc-600 font-mono-data text-xs">{new Date(c.updated_at).toLocaleDateString()}</td>
                <td className="px-4 py-2.5 text-right">
                  <Link to={`/command-center/studio/${c.id}`} data-testid={`edit-content-${c.id}`} className="text-emerald-400 inline-flex items-center gap-1">Edit <ArrowRight className="h-3.5 w-3.5" /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New Content">
        <form onSubmit={create} className="space-y-4">
          <Field label="Type">
            <select data-testid="content-type-input" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Title"><input data-testid="content-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Summary"><textarea className={inputCls} rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <Btn data-testid="content-create-btn" type="submit" className="w-full justify-center">Create &amp; Edit</Btn>
        </form>
      </Modal>
    </div>
  );
}
